import {
  memo,
  startTransition,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState
} from 'react';
import { isEmpty, uniqueId } from 'lodash-es';
import { Box } from '@alf/uikit';
import { useI18n } from '@namespace/i18n';
import {
  IS_USE_PLEX_API,
  REAL_TIME_WINS_CAROUSEL_ID
} from '../../../../constants';
import {
  realTimeWins,
  useGetGameURL,
  useGetLoadFeedGamesCallback
} from '../../../../hooks';
import { useGamesData, useGamesLaunchIdToGameId } from '../../../../store';
import {
  CASINO_TYPE_FOR_REQUEST,
  DEFAULT_SLIDER_INTERVAL,
  LIMIT,
  SLIDER_ITEMS_LENGTH
} from '../constants';

import styles from './index.module.css';

const getRealTimeWinCardWidth = () => {
  const rootStyles = getComputedStyle(document.documentElement);
  const cardWidth = parseFloat(rootStyles.getPropertyValue('--card-width'));
  const cardGap = parseFloat(rootStyles.getPropertyValue('--card-gap'));
  return cardWidth + cardGap;
};

export const RealTimeWinsList = memo(
  ({
    casinoType,
    delay: widgetSlideDelay,
    headerComponent: HeaderComponent = null,
    itemComponent: ItemComponent = null,
    classNames = {},
    sliderItemsLength = SLIDER_ITEMS_LENGTH
  }) => {
    const { t } = useI18n();
    const launchIdToGameId = useGamesLaunchIdToGameId();
    const gamesList = useGamesData();
    const getGameURL = useGetGameURL();
    const fetchGamesPlex = useGetLoadFeedGamesCallback();

    const cardsAmountInOneRotationCycle = Math.ceil(sliderItemsLength / 2);

    // rotationInterval = time during which a card will move by its width+gap
    // so, if rotation interval is 3000ms - card will be moved in a carousel by its width+gap during 3000ms
    const rotationInterval =
      (widgetSlideDelay || DEFAULT_SLIDER_INTERVAL) * 1000;
    const { realTimeWinsTitleWrapper = '' } = classNames;

    const [carouselCurrentItems, setCarouselCurrentItems] = useState([]);

    const nextRealTimeWinsItemsRef = useRef([]);
    const carouselRef = useRef(null);

    const getRealtimeWinGame = useCallback(
      async (isInitialCall) => {
        const {
          data: { items: fetchedWins = [] }
        } = await realTimeWins({
          limit: LIMIT,
          casinoType: CASINO_TYPE_FOR_REQUEST[casinoType]
        });

        if (IS_USE_PLEX_API && !isEmpty(fetchedWins)) {
          await fetchGamesPlex({
            filter: {
              launchId: fetchedWins
                .map(({ internalGameId }) => internalGameId)
                .slice(0, LIMIT),
              limit: LIMIT
            }
          });
        }

        startTransition(() => {
          const preparedWins = fetchedWins
            .slice(0, sliderItemsLength)
            .map((item) => ({
              ...item,
              key: uniqueId('item_')
            }));

          if (isInitialCall) {
            setCarouselCurrentItems(preparedWins);
          } else {
            nextRealTimeWinsItemsRef.current = [
              ...nextRealTimeWinsItemsRef.current,
              ...preparedWins
            ];
          }
        });
      },
      [casinoType, fetchGamesPlex, sliderItemsLength]
    );

    const rotationCycleDurationMs =
      rotationInterval * cardsAmountInOneRotationCycle;

    useEffect(() => {
      getRealtimeWinGame(true);
      // for getting second pack of real-time-wins after half the rotation period
      const timeoutId = setTimeout(
        () => getRealtimeWinGame(),
        rotationCycleDurationMs / 2
      );
      // at the start of each period next pack of real-time-wins will be requested (1 pack - 2 rotation cycles)
      // so we are sure that we always have win-items for future
      const intervalId = setInterval(
        () => getRealtimeWinGame(),
        rotationCycleDurationMs * 2
      );
      return () => {
        clearTimeout(timeoutId);
        clearInterval(intervalId);
      };
    }, []);

    // useLayoutEffect to be sure that carouselItems are in DOM
    useLayoutEffect(() => {
      if (carouselCurrentItems.length) {
        let animationFrameId;
        let startTime = null;

        const itemWidth = getRealTimeWinCardWidth(); // width of a real-time-win card (including right gap)

        const updateCarousel = (timestamp) => {
          const runAnimation = () => {
            if (startTime) {
              setCarouselCurrentItems((prev) => [
                ...prev.slice(cardsAmountInOneRotationCycle),
                ...nextRealTimeWinsItemsRef.current.slice(
                  0,
                  cardsAmountInOneRotationCycle
                )
              ]);
              // remove real-wins items from ref, which were added to a carouselCurrentItemsState
              nextRealTimeWinsItemsRef.current =
                nextRealTimeWinsItemsRef.current.slice(
                  cardsAmountInOneRotationCycle
                );
            }

            if (carouselRef.current) {
              // Reset position immediately without transition
              carouselRef.current.style.transition = 'none';
              carouselRef.current.style.transform = `translateX(0px)`;

              // Force reflow to apply the reset before adding transition back
              carouselRef.current.offsetWidth;
              // Apply transition again for smooth movement
              carouselRef.current.style.transition = `transform ${rotationCycleDurationMs}ms linear`;
              carouselRef.current.style.transform = `translateX(-${cardsAmountInOneRotationCycle * itemWidth}px)`;
            }

            startTime = timestamp;
          };

          // initial animation run
          if (!startTime) {
            runAnimation();
          }
          const elapsedTime = timestamp - startTime;

          // re-run animation if previous is finished
          if (elapsedTime >= rotationCycleDurationMs) {
            runAnimation();
          }
          animationFrameId = requestAnimationFrame(updateCarousel);
        };
        const timeoutId = setTimeout(() => {
          animationFrameId = requestAnimationFrame(updateCarousel);
        }, 0);

        return () => {
          if (animationFrameId) {
            cancelAnimationFrame(animationFrameId);
          }
          clearTimeout(timeoutId);
        };
      }
      return undefined;
    }, [carouselCurrentItems.length]);

    if (!carouselCurrentItems.length) {
      return null;
    }

    return (
      <>
        <HeaderComponent
          title={t('web.games.widget.wins')}
          classNames={{
            headerTitleWrapperClassName: styles.headerTitleWrapperClassName,
            realTimeWinsTitleWrapper
          }}
        />
        <Box
          id={REAL_TIME_WINS_CAROUSEL_ID}
          className={styles.carouselContainer}
        >
          <Box className={styles.carouselContent} ref={carouselRef}>
            {carouselCurrentItems.map((item) => (
              <ItemComponent
                key={item.key}
                {...item}
                rotationInterval={rotationInterval}
                game={gamesList[launchIdToGameId[item.internalGameId]]}
                casinoType={casinoType}
                getGameURL={getGameURL}
              />
            ))}
          </Box>
        </Box>
      </>
    );
  }
);
